import * as ExcelJS from 'exceljs';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import {
  getTotalDepositAmount,
  getTotalWithdrawalAmount,
  getBalanceDifference,
} from '../../component/maturity-schedule/MaturityTableUtils';
import { BaseExcelService } from './baseExcelService';
import { ExcelColors } from './types';

/**
 * Service for exporting single maturity schedule to Excel
 */
export class SingleMaturityExportService {
  /**
   * Export single maturity schedule to Excel invoice
   */
  static async exportToExcel(record: MaturityScheduleResponse): Promise<void> {
    console.log('Export Excel - Record data:', record);

    // Calculate amounts using utility functions
    const totalDeposit = getTotalDepositAmount(record);
    const totalWithdrawal = getTotalWithdrawalAmount(record);
    const balanceDiff = getBalanceDifference(record);
    const feeRate = record.feeApplied || 0;
    const feeAmount = (totalWithdrawal * feeRate) / 100;
    const totalAmount = feeAmount + balanceDiff;

    console.log('Calculated values:', {
      repaymentAmount: record.repaymentAmount,
      totalWithdrawal,
      balanceDiff,
      feeRate,
      feeAmount,
      totalAmount,
    });

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Hóa đơn');

    // Set column widths
    worksheet.columns = [
      { width: 25 }, // A - Dịch vụ
      { width: 15 }, // B - Số tiền
      { width: 12 }, // C - Đơn vị
      { width: 10 }, // D - Phí (%)
    ];

    // 1. Main title
    worksheet.mergeCells('A1:D1');
    BaseExcelService.createHeaderCell(
      worksheet,
      'A1',
      'HÓA ĐƠN DỊCH VỤ ĐÁO HẠN THẺ TÍN DỤNG',
      16,
      ExcelColors.PRIMARY_HEADER
    );

    // 2. Export date
    SingleMaturityExportService.addExportDate(worksheet);

    // 3. Customer information section
    SingleMaturityExportService.addCustomerInfo(worksheet, record);

    // 4. Service details section
    SingleMaturityExportService.addServiceDetails(worksheet, {
      totalDeposit,
      totalWithdrawal,
      feeAmount,
      feeRate,
      balanceDiff,
      depositAmount: totalDeposit, // Thêm depositAmount để sử dụng cho "Tổng đáo"
    });

    // 5. Total amount
    SingleMaturityExportService.addTotalAmount(worksheet, totalAmount);

    // 6. Notes
    SingleMaturityExportService.addNotes(worksheet, record.notes);

    // Generate and save file
    const fileName = SingleMaturityExportService.generateFileName(record);
    await BaseExcelService.saveExcelFile(workbook, fileName);
  }

  /**
   * Add export date section
   */
  private static addExportDate(worksheet: ExcelJS.Worksheet): void {
    worksheet.getCell('A3').value = 'Ngày xuất:';
    worksheet.getCell('B3').value = BaseExcelService.formatVietnameseDate(
      new Date()
    );
  }

  /**
   * Add customer information section
   */
  private static addCustomerInfo(
    worksheet: ExcelJS.Worksheet,
    record: MaturityScheduleResponse
  ): void {
    // Customer header
    worksheet.mergeCells('A5:D5');
    BaseExcelService.createHeaderCell(
      worksheet,
      'A5',
      'THÔNG TIN KHÁCH HÀNG',
      12,
      ExcelColors.SECONDARY_HEADER
    );

    // Customer details
    const customerInfo = [
      ['Tên khách hàng:', record.creditCard?.customer?.name || 'N/A'],
      ['Số thẻ:', record.creditCard?.cardNumber || 'N/A'],
      ['Ngân hàng:', record.creditCard?.bank?.name || 'N/A'],
      ['Chủ thẻ:', record.creditCard?.cardHolderName || 'N/A'],
      ['Loại thẻ:', record.creditCard?.cardType?.name || 'N/A'],
      [
        'Ngày đáo hạn:',
        BaseExcelService.formatVietnameseDate(new Date(record.dueDate)),
      ],
    ];

    customerInfo.forEach((info, index) => {
      const row = 6 + index;
      worksheet.getCell(`A${row}`).value = info[0];
      worksheet.getCell(`A${row}`).font = { bold: true };
      worksheet.getCell(`B${row}`).value = info[1];
    });
  }

  /**
   * Add service details section
   */
  private static addServiceDetails(
    worksheet: ExcelJS.Worksheet,
    amounts: {
      totalDeposit: number;
      totalWithdrawal: number;
      feeAmount: number;
      feeRate: number;
      balanceDiff: number;
      depositAmount: number; // Thêm depositAmount để sử dụng cho "Tổng đáo"
    }
  ): void {
    // Service header
    worksheet.mergeCells('A13:D13');
    BaseExcelService.createHeaderCell(
      worksheet,
      'A13',
      'CHI TIẾT DỊCH VỤ',
      12,
      ExcelColors.SECONDARY_HEADER
    );

    // Table headers
    const tableHeaders = ['Dịch vụ', 'Số tiền', 'Đơn vị', 'Phí (%)'];
    BaseExcelService.createTableHeader(worksheet, 15, tableHeaders);

    // Table data
    const tableData = [
      ['Tổng đáo', amounts.depositAmount, 'VND', ''],
      ['Tổng rút', amounts.totalWithdrawal, 'VND', ''],
      ['Phí dịch vụ', amounts.feeAmount, 'VND', amounts.feeRate],
      ['Chênh lệch', amounts.balanceDiff, 'VND', ''],
    ];

    tableData.forEach((rowData, rowIndex) => {
      const row = 16 + rowIndex;
      rowData.forEach((cellData, colIndex) => {
        const cell = worksheet.getCell(row, colIndex + 1);
        cell.value = cellData;

        // Format currency
        if (colIndex === 1 && typeof cellData === 'number') {
          BaseExcelService.formatCurrencyCell(cell, cellData);
        }
        // Format percentage
        else if (colIndex === 3 && typeof cellData === 'number') {
          BaseExcelService.formatPercentageCell(cell, cellData);
        }
        // Apply border for other cells
        else {
          BaseExcelService.applyBorder(cell);
        }
      });
    });
  }

  /**
   * Add total amount section
   */
  private static addTotalAmount(
    worksheet: ExcelJS.Worksheet,
    totalAmount: number
  ): void {
    const totalRow = 21;

    // Total label
    const labelCell = worksheet.getCell(`A${totalRow}`);
    labelCell.value = 'TỔNG THÀNH TIỀN';
    BaseExcelService.applyCellStyle(labelCell, {
      font: { bold: true, color: { argb: ExcelColors.WHITE_TEXT } },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: ExcelColors.PRIMARY_HEADER },
      },
    });

    // Total amount
    const amountCell = worksheet.getCell(`B${totalRow}`);
    amountCell.value = totalAmount;
    amountCell.numFmt = '#,##0';
    BaseExcelService.applyCellStyle(amountCell, {
      font: { bold: true, color: { argb: ExcelColors.WHITE_TEXT } },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: ExcelColors.PRIMARY_HEADER },
      },
    });

    // Apply border to all cells in total row
    ['A', 'B', 'C', 'D'].forEach((col) => {
      BaseExcelService.applyBorder(worksheet.getCell(`${col}${totalRow}`));
    });
  }

  /**
   * Add notes section
   */
  private static addNotes(worksheet: ExcelJS.Worksheet, notes?: string): void {
    worksheet.getCell('A23').value = 'Ghi chú:';
    worksheet.getCell('A23').font = { bold: true };
    worksheet.getCell('B23').value = notes || 'Không có ghi chú';
  }

  /**
   * Generate file name for single export
   */
  private static generateFileName(record: MaturityScheduleResponse): string {
    const cardNumber = record.creditCard?.cardNumber?.slice(-4) || 'XXXX';
    const customerName =
      record.creditCard?.customer?.name?.replace(/[^\w\s]/gi, '') || 'Customer';
    return BaseExcelService.generateFileName(
      `HoaDon_${customerName}_${cardNumber}`
    );
  }
}
